import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  SAVED_DEPLOYMENT_CONFIG_ANONYMOUS_USER,
  SAVED_DEPLOYMENT_CONFIGS_STORAGE_KEY,
  createSavedConfigRecord,
  clearConfigsForUser,
  deleteSavedConfigFromStorage,
  findConfigByName,
  listConfigsForUser,
  parseSavedDeploymentConfigStorage,
  updateSavedConfigInStorage,
  upsertSavedConfigInStorage
} from "@/utils/savedDeploymentConfig";
import {
  SavedDeploymentConfig,
  SavedDeploymentConfigKind,
  SavedDeploymentConfigPayload,
  SavedDeploymentConfigStorage
} from "@/types/savedDeploymentConfig";
import { useUserStore } from "@/store/user";

type CreateConfigArgs = {
  name: string;
  kind: SavedDeploymentConfigKind;
  payload: SavedDeploymentConfigPayload;
  /** When set, replace this id (update in place) */
  overwriteId?: string;
};

type UseSavedDeploymentConfigStore = {
  storage: SavedDeploymentConfigStorage;
  list: (kind?: SavedDeploymentConfigKind, userId?: string) => SavedDeploymentConfig[];
  getById: (id: string, userId?: string) => SavedDeploymentConfig | undefined;
  findByName: (
    kind: SavedDeploymentConfigKind,
    name: string,
    excludeId?: string,
    userId?: string
  ) => SavedDeploymentConfig | undefined;
  create: (args: CreateConfigArgs, userId?: string) => SavedDeploymentConfig;
  update: (
    id: string,
    patch: Partial<Pick<SavedDeploymentConfig, "name" | "payload">>,
    userId?: string
  ) => SavedDeploymentConfig | undefined;
  remove: (id: string, userId?: string) => void;
  clearForUser: (userId?: string) => void;
};

const resolveUserId = (userId?: string) => {
  if (userId) return userId;
  const fromStore = useUserStore.getState().userId;
  return fromStore || SAVED_DEPLOYMENT_CONFIG_ANONYMOUS_USER;
};

export const useSavedDeploymentConfigStore = create<UseSavedDeploymentConfigStore>()(
  persist(
    (set, get) => ({
      storage: { version: 1, byUserId: {} },

      list: (kind, userId) => listConfigsForUser(get().storage, resolveUserId(userId), kind),

      getById: (id, userId) => {
        const uid = resolveUserId(userId);
        return (get().storage.byUserId[uid] ?? []).find((c) => c.id === id);
      },

      findByName: (kind, name, excludeId, userId) =>
        findConfigByName(get().storage, resolveUserId(userId), kind, name, excludeId),

      create: (args, userId) => {
        const uid = resolveUserId(userId);
        const record = createSavedConfigRecord({
          name: args.name,
          kind: args.kind,
          payload: args.payload,
          id: args.overwriteId
        });
        // If overwriting, preserve createdAt from previous record
        let finalRecord = record;
        if (args.overwriteId) {
          const previous = (get().storage.byUserId[uid] ?? []).find(
            (c) => c.id === args.overwriteId
          );
          if (previous) {
            finalRecord = {
              ...record,
              id: previous.id,
              createdAt: previous.createdAt,
              updatedAt: new Date().toISOString()
            };
          }
        }
        set({
          storage: upsertSavedConfigInStorage(get().storage, uid, finalRecord, {
            overwriteId: args.overwriteId
          })
        });
        return finalRecord;
      },

      update: (id, patch, userId) => {
        const uid = resolveUserId(userId);
        const nextStorage = updateSavedConfigInStorage(get().storage, uid, id, patch);
        set({ storage: nextStorage });
        return (nextStorage.byUserId[uid] ?? []).find((c) => c.id === id);
      },

      remove: (id, userId) => {
        const uid = resolveUserId(userId);
        set({ storage: deleteSavedConfigFromStorage(get().storage, uid, id) });
      },

      clearForUser: (userId) => {
        const uid = userId ?? useUserStore.getState().userId;
        if (!uid) return;
        set({ storage: clearConfigsForUser(get().storage, uid) });
      }
    }),
    {
      name: SAVED_DEPLOYMENT_CONFIGS_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ storage: state.storage }),
      merge: (persisted, current) => {
        const parsed = parseSavedDeploymentConfigStorage(
          (persisted as { storage?: unknown } | undefined)?.storage ?? persisted
        );
        return {
          ...current,
          storage: parsed
        };
      }
    }
  )
);
